import {
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";

/** Return today at midnight in the user's local timezone. */
export const today = () => startOfDay(new Date());

/** Parse an ISO date string ("YYYY-MM-DD") into a local Date at midnight. */
export const parseDueDate = (iso) => {
  if (!iso) return null;
  // Force local midnight to avoid TZ shifts from parseISO on date-only strings.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isValid(dt) ? dt : null;
};

/** Days between today and the due date (positive = future, negative = past). */
export const daysUntilDue = (iso) => {
  const due = parseDueDate(iso);
  if (!due) return null;
  return differenceInCalendarDays(due, today());
};

/**
 * Compute derived status for display purposes only. Never persisted.
 * Returns one of: "Completed" | "Overdue" | "Due Soon" | "Pending".
 */
export const computeDerivedStatus = (task) => {
  if (!task) return "Pending";
  if (task.status === "Completed") return "Completed";
  const diff = daysUntilDue(task.dueDate);
  if (diff === null) return "Pending";
  if (diff < 0) return "Overdue";
  if (diff <= 7) return "Due Soon";
  return "Pending";
};

export const STATUS_COLOR = {
  Completed: {
    bg: "#DCFCE7",
    fg: "#166534",
    dot: "#16A34A",
    label: "Completed",
  },
  Overdue: {
    bg: "#FEE2E2",
    fg: "#991B1B",
    dot: "#DC2626",
    label: "Overdue",
  },
  "Due Soon": {
    bg: "#FEF3C7",
    fg: "#92400E",
    dot: "#F59E0B",
    label: "Due Soon",
  },
  Pending: {
    bg: "#F1F5F9",
    fg: "#334155",
    dot: "#94A3B8",
    label: "Pending",
  },
};

export const formatDueDate = (iso) => {
  const d = parseDueDate(iso);
  return d ? format(d, "dd MMM yyyy") : "—";
};

export const formatUpdatedAt = (iso) => {
  if (!iso) return "—";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "dd MMM yyyy, HH:mm") : "—";
};

export const formatToday = () => format(today(), "EEEE, dd MMMM yyyy");
export const formatClock = (d = new Date()) => format(d, "HH:mm:ss");

export const daysRemainingLabel = (task) => {
  if (task.status === "Completed") return "Completed";
  const diff = daysUntilDue(task.dueDate);
  if (diff === null) return "—";
  if (diff === 0) return "Today";
  if (diff === 1) return "1 day left";
  if (diff > 1) return `${diff} days left`;
  if (diff === -1) return "Overdue by 1 day";
  return `Overdue by ${Math.abs(diff)} days`;
};
